/**
 * Discount Price Enrichment
 *
 * Uses the Storefront API cartCreate mutation to detect automatic discounts
 * and update product prices across the site (cards, product page, etc.).
 */
(function () {
  'use strict';

  const CONFIG = window.discountPriceEnrichment;
  if (!CONFIG || !CONFIG.storefrontAccessToken || CONFIG.storefrontAccessToken.includes('placeholder')) {
    console.warn('[DiscountPriceEnrichment] Missing or placeholder Storefront API token. Skipping.');
    return;
  }

  const CACHE_KEY = 'dpe_cache';
  const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  const API_URL = `https://${CONFIG.shopDomain}/api/${CONFIG.apiVersion}/graphql.json`;

  // --- Money formatting ---

  function formatMoney(amountInCents) {
    const euros = Math.floor(amountInCents / 100);
    const cents = amountInCents % 100;
    const formatted = cents === 0
      ? `€${euros.toLocaleString('nl-NL')},-`
      : `€${euros.toLocaleString('nl-NL')},${String(cents).padStart(2, '0')}`;
    return formatted;
  }

  function formatMoneyWithCurrency(amountInCents) {
    const base = formatMoney(amountInCents);
    return base + ' EUR';
  }

  function formatPrice(amountInCents) {
    return CONFIG.currencyCodeEnabled
      ? formatMoneyWithCurrency(amountInCents)
      : formatMoney(amountInCents);
  }

  function storefrontAmountToCents(amount) {
    return Math.round(parseFloat(amount) * 100);
  }

  // --- Cache ---

  function getCache() {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (Date.now() - data.timestamp > CACHE_TTL) {
        sessionStorage.removeItem(CACHE_KEY);
        return null;
      }
      return data.entries;
    } catch {
      return null;
    }
  }

  function setCache(entries) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({
        timestamp: Date.now(),
        entries: entries
      }));
    } catch {
      // sessionStorage full or unavailable
    }
  }

  function mergeCache(newEntries) {
    const existing = getCache() || {};
    const merged = Object.assign({}, existing, newEntries);
    setCache(merged);
    return merged;
  }

  // --- Storefront API ---

  function buildCartCreateMutation(variantIds) {
    const lines = variantIds.map(function (vid) {
      return `{ merchandiseId: "gid://shopify/ProductVariant/${vid}", quantity: 1 }`;
    });

    return `mutation {
      cartCreate(input: { lines: [${lines.join(', ')}] }) {
        cart {
          lines(first: 250) {
            edges {
              node {
                merchandise {
                  ... on ProductVariant {
                    id
                  }
                }
                cost {
                  subtotalAmount { amount currencyCode }
                  totalAmount { amount currencyCode }
                }
              }
            }
          }
        }
        userErrors { field message }
      }
    }`;
  }

  async function fetchDiscountedPrices(variantIds) {
    if (!variantIds.length) return {};

    const query = buildCartCreateMutation(variantIds);

    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': CONFIG.storefrontAccessToken
      },
      body: JSON.stringify({ query: query })
    });

    if (!response.ok) {
      console.warn('[DiscountPriceEnrichment] API request failed:', response.status);
      return {};
    }

    const json = await response.json();

    if (json.errors) {
      console.warn('[DiscountPriceEnrichment] GraphQL errors:', json.errors);
      return {};
    }

    const cart = json.data && json.data.cartCreate && json.data.cartCreate.cart;
    if (!cart) return {};

    var results = {};
    var edges = cart.lines.edges;

    for (var i = 0; i < edges.length; i++) {
      var node = edges[i].node;
      var gid = node.merchandise.id;
      var numericId = gid.replace('gid://shopify/ProductVariant/', '');
      var subtotal = storefrontAmountToCents(node.cost.subtotalAmount.amount);
      var total = storefrontAmountToCents(node.cost.totalAmount.amount);

      results[numericId] = {
        originalPrice: subtotal,
        discountedPrice: total,
        hasDiscount: total < subtotal
      };
    }

    return results;
  }

  // --- DOM updates ---

  function updatePriceElement(priceEl, originalPriceCents, discountedPriceCents) {
    var existingCompare = parseInt(priceEl.getAttribute('data-compare-at-price'), 10);
    var existingPrice = parseInt(priceEl.getAttribute('data-price'), 10);

    // If already on sale via compare_at_price, only update if automatic discount gives lower price
    if (existingCompare && existingCompare > existingPrice) {
      if (discountedPriceCents >= existingPrice) {
        return; // existing sale is better or equal
      }
      // Automatic discount beats compare_at_price sale — use compare_at as original
      originalPriceCents = existingCompare;
    }

    var savingsAmount = originalPriceCents - discountedPriceCents;
    var discountPercentage = Math.round((savingsAmount / originalPriceCents) * 100);

    // Add sale class
    priceEl.classList.add('price-on-sale');

    // Build sale HTML
    var originalFormatted = CONFIG.currencyCodeEnabled
      ? formatMoneyWithCurrency(originalPriceCents)
      : formatMoney(originalPriceCents);
    var discountedFormatted = formatPrice(discountedPriceCents);
    var savingsFormatted = CONFIG.currencyCodeEnabled
      ? formatMoneyWithCurrency(savingsAmount)
      : formatMoney(savingsAmount);

    var taxSuffix = '';
    if (CONFIG.priceTaxSuffix && CONFIG.taxesIncluded) {
      taxSuffix = '<span class="sup">' + CONFIG.priceTaxSuffix + '</span>';
    }

    var saleHTML =
      '<div class="price-sale">' +
        '<div class="price-sale-row">' +
          '<span class="visually-hidden">Normale prijs</span>' +
          '<s class="price-item price-item-regular">' + originalFormatted + '</s>' +
          '<span class="visually-hidden">Aanbiedingsprijs</span>' +
          '<span class="price-item price-item-sale price-item-last">' +
            discountedFormatted + taxSuffix +
          '</span>' +
        '</div>' +
        '<div class="price-savings">' +
          '<span class="price-savings-label">Je bespaart:</span>' +
          '<span class="price-savings-amount">' + savingsFormatted + '</span>' +
          '<span class="price-savings-percentage">(' + discountPercentage + '% korting)</span>' +
        '</div>' +
      '</div>';

    // Remove any existing enriched sale div
    var existingSale = priceEl.querySelector('.price-sale[data-enriched]');
    if (existingSale) existingSale.remove();

    // Also remove the original .price-sale if server-rendered (to avoid duplicates when we override)
    var serverSale = priceEl.querySelector('.price-sale:not([data-enriched])');

    // Insert our enriched sale div
    var container = priceEl.querySelector('.price-container');
    if (container) {
      // If there was a server-rendered sale, remove it since we're replacing
      if (serverSale) serverSale.remove();

      var temp = document.createElement('div');
      temp.innerHTML = saleHTML;
      var saleEl = temp.firstElementChild;
      saleEl.setAttribute('data-enriched', 'true');
      container.appendChild(saleEl);
    }

    // Update data attributes for future reference
    priceEl.setAttribute('data-enriched-price', discountedPriceCents);
  }

  function enrichCards(discountData) {
    var cards = document.querySelectorAll('product-card[data-variant-id]');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var variantId = card.getAttribute('data-variant-id');
      var info = discountData[variantId];
      if (!info || !info.hasDiscount) continue;

      // Skip if already enriched with same price
      var priceEl = card.querySelector('.price');
      if (!priceEl) continue;
      if (priceEl.getAttribute('data-enriched-price') === String(info.discountedPrice)) continue;

      updatePriceElement(priceEl, info.originalPrice, info.discountedPrice);
    }
  }

  function enrichProductPage(discountData) {
    // Product page has price rendered via price.liquid inside [id^="price-"]
    var priceSections = document.querySelectorAll('[id^="price-"] .price');
    for (var i = 0; i < priceSections.length; i++) {
      var priceEl = priceSections[i];
      var priceAttr = priceEl.getAttribute('data-price');
      if (!priceAttr) continue;

      // Find the variant ID from the product form
      var section = priceEl.closest('[id^="price-"]');
      if (!section) continue;
      var sectionId = section.id.replace('price-', '');
      var form = document.getElementById('product-form-' + sectionId);
      if (!form) continue;
      var input = form.querySelector('input[name="id"]');
      if (!input) continue;
      var variantId = input.value;

      var info = discountData[variantId];
      if (!info || !info.hasDiscount) continue;
      if (priceEl.getAttribute('data-enriched-price') === String(info.discountedPrice)) continue;

      updatePriceElement(priceEl, info.originalPrice, info.discountedPrice);
    }
  }

  // --- Main logic ---

  function collectVariantIds() {
    var ids = new Set();

    // From product cards
    var cards = document.querySelectorAll('product-card[data-variant-id]');
    for (var i = 0; i < cards.length; i++) {
      var id = cards[i].getAttribute('data-variant-id');
      if (id) ids.add(id);
    }

    // From product page form
    var forms = document.querySelectorAll('[id^="product-form-"]');
    for (var j = 0; j < forms.length; j++) {
      var input = forms[j].querySelector('input[name="id"]');
      if (input && input.value) ids.add(input.value);
    }

    return Array.from(ids);
  }

  async function enrichAll() {
    var allIds = collectVariantIds();
    if (!allIds.length) return;

    // Check cache first
    var cached = getCache();
    var uncachedIds = [];

    if (cached) {
      for (var i = 0; i < allIds.length; i++) {
        if (!(allIds[i] in cached)) {
          uncachedIds.push(allIds[i]);
        }
      }
    } else {
      uncachedIds = allIds;
    }

    var discountData = cached || {};

    if (uncachedIds.length > 0) {
      // Batch in groups of 250 (Storefront API limit)
      for (var start = 0; start < uncachedIds.length; start += 250) {
        var batch = uncachedIds.slice(start, start + 250);
        try {
          var batchResults = await fetchDiscountedPrices(batch);
          // Also store non-discounted entries so we don't re-fetch them
          for (var b = 0; b < batch.length; b++) {
            if (!(batch[b] in batchResults)) {
              batchResults[batch[b]] = { originalPrice: 0, discountedPrice: 0, hasDiscount: false };
            }
          }
          Object.assign(discountData, batchResults);
        } catch (err) {
          console.warn('[DiscountPriceEnrichment] Failed to fetch batch:', err);
        }
      }
      mergeCache(discountData);
    }

    enrichCards(discountData);
    enrichProductPage(discountData);
  }

  // --- Variant change handler ---

  async function handleVariantChange(data) {
    if (!data || !data.variant || !data.variant.id) return;

    var variantId = String(data.variant.id);
    var cached = getCache();
    var discountData = {};

    if (cached && variantId in cached) {
      discountData = cached;
    } else {
      try {
        var results = await fetchDiscountedPrices([variantId]);
        if (!(variantId in results)) {
          results[variantId] = { originalPrice: 0, discountedPrice: 0, hasDiscount: false };
        }
        discountData = mergeCache(results);
      } catch (err) {
        console.warn('[DiscountPriceEnrichment] Variant change fetch failed:', err);
        return;
      }
    }

    // Wait a tick for the theme's own variant change DOM update to complete
    setTimeout(function () {
      enrichProductPage(discountData);
    }, 100);
  }

  // --- Observers ---

  var observerDebounce = null;

  function onDOMMutation() {
    clearTimeout(observerDebounce);
    observerDebounce = setTimeout(function () {
      enrichAll();
    }, 300);
  }

  function setupObservers() {
    // Observe ProductGridContainer for AJAX filtering/pagination
    var gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer) {
      new MutationObserver(onDOMMutation).observe(gridContainer, {
        childList: true,
        subtree: true
      });
    }

    // Observe body for dynamically loaded cards (recommendations, recently viewed)
    new MutationObserver(function (mutations) {
      var hasNewCards = false;
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.tagName === 'PRODUCT-CARD' || node.querySelector && node.querySelector('product-card[data-variant-id]')) {
            hasNewCards = true;
            break;
          }
        }
        if (hasNewCards) break;
      }
      if (hasNewCards) onDOMMutation();
    }).observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // --- Init ---

  function init() {
    enrichAll();
    setupObservers();

    // Listen for variant changes on product page
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.productVariantChange, handleVariantChange);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
